import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useEffect,
} from 'react';
import { useHistory } from 'react-router-dom';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { DB, Realtime } from '~/services/firebase';

import ICompany from '~/interfaces/ICompany';
import ITable from '~/interfaces/ITable';
import { bff } from '~/services/bff';
import IDistance from '~/interfaces/IDistance';
import { useClients } from './clients';

interface RealtimeData {
  status: boolean;
}

interface SettingsContextData {
  company: ICompany;
  table: ITable;
  distances: IDistance[] | null;
  loadTable(tableId: string): Promise<void>;
  loadCompany(): Promise<void>;
  updateCompany(data: ICompany): void;
  loadDistances(id: string): Promise<void>;
}

const SettingsContext = createContext<SettingsContextData>(
  {} as SettingsContextData,
);

const { REACT_APP_FIREBASE_PROJECT } = process.env;

export const SettingsProvider: React.FC = ({ children }) => {
  const history = useHistory();
  const [company, setCompany] = useState({} as ICompany);
  const [table, setTable] = useState({} as ITable);
  const [distances, setDistances] = useState<IDistance[] | null>(null);

  const loadDistances = useCallback(async (company_id: string) => {
    const q = query(
      collection(DB, 'distances'),
      where('company_id', '==', company_id),
    );
    const querySnapshot = await getDocs(q);

    const items: IDistance[] = [];

    querySnapshot.docs.forEach(item => {
      const parseItem = {
        ...item.data(),
        uid: item.id,
      } as IDistance;

      items.push(parseItem);
    });

    const sorted = items.sort((a, b) =>
      a.district?.toLocaleLowerCase() > b.district?.toLocaleLowerCase()
        ? 1
        : b.district?.toLocaleLowerCase() > a.district?.toLocaleLowerCase()
        ? -1
        : 0,
    );

    setDistances(sorted);
  }, []);

  // useEffect(() => {
  //   if (distances === null && company.uid) {
  //     loadDistances();
  //   }
  // }, [company, distances, loadDistances]);

  const updateCompany = useCallback((data: ICompany) => {
    setCompany(data);
  }, []);

  const loadTable = useCallback(async (tableId: string) => {
    const docSnap = await getDoc(doc(DB, 'tables', tableId));
    const item = docSnap.data();

    setTable({
      ...item,
    } as ITable);
  }, []);

  const loadCompany = useCallback(async () => {
    try {
      const response = await bff.get(
        `/companies/${REACT_APP_FIREBASE_PROJECT}`,
      );

      setCompany(response.data);

      const ref = Realtime.ref(`status/${response.data.uid}`);
      ref.on('value', snapshot => {
        const value = snapshot.val();
        if (value) {
          const { status } = value as RealtimeData;
          setCompany(current => ({
            ...current,
            opened: status,
          }));
        }
      });
    } catch (err) {
      console.log(err);
      history.push('/');
    }
  }, [history]);

  return (
    <SettingsContext.Provider
      value={{
        company,
        distances,
        loadCompany,
        loadTable,
        table,
        updateCompany,
        loadDistances,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export function useSettings(): SettingsContextData {
  const context = useContext(SettingsContext);
  return context;
}
